'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { DescriptionList, DescriptionListItem } from '@//components/DescriptionList';
import { Link } from '@//components/Link';
import { Tabs, type TabsSectionProps } from '@//components/Tabs';
import useEnhancedProperties, {
  defaultEnhancedPropertiesOptions,
} from '@//utils/enhancedProperties/useEnhancedProperties';
import useExtraProperties from '@//utils/enhancedProperties/useExtraProperties';
import styles from './VehicleDetailsTabs.module.scss';

interface VehicleDetailsTabsProps {
  vehicle: ElasticVehicle;
}

export const VehicleDetailsTabs = ({ vehicle }: VehicleDetailsTabsProps) => {
  const tComponents = useTranslations('components.vehicleDetailsModal');

  const extraProperties = useExtraProperties(vehicle);
  const enhancedProperties = useEnhancedProperties(vehicle.enhancedProperties, {
    ...defaultEnhancedPropertiesOptions,
    extraProperties,
  });

  const propertyLink = useCallback(
    (propertyName: string) => {
      const extra = extraProperties.find((p) => p.name === propertyName && p.href && p.title);
      if (!extra?.href || !extra?.title) return null;
      return { href: extra.href, title: extra.title };
    },
    [extraProperties],
  );

  const tabs: TabsSectionProps[] = useMemo(() => {
    const result: TabsSectionProps[] = [];

    for (const enhancedProperty of enhancedProperties) {
      const categoryLabel =
        enhancedProperty.name === '_uncategorised'
          ? tComponents('uncategorised')
          : enhancedProperty.name;

      result.push({
        label: categoryLabel,
        children: (
          <div role="group" aria-label={categoryLabel} className={styles.group}>
            {enhancedProperty.subCategories.map((subCategory) => {
              const subCategoryLabel =
                subCategory.name === '_uncategorised'
                  ? tComponents('uncategorised')
                  : subCategory.name;
              const sectionLabel =
                enhancedProperty.subCategories.length > 1 ? subCategoryLabel : categoryLabel;

              return (
                <section
                  key={subCategory.name}
                  className={styles.section}
                  aria-label={sectionLabel}
                >
                  {enhancedProperty.subCategories.length > 1 && (
                    <h3 className={styles.heading}>{subCategoryLabel}</h3>
                  )}
                  <DescriptionList variant="equal-width">
                    {subCategory.items.map((property) => {
                      const link = propertyLink(property.name);
                      return (
                        <DescriptionListItem
                          key={`${enhancedProperty.id}-${subCategory.id}-${property.key}`}
                          term={property.name}
                        >
                          {link ? (
                            <Link href={link.href} title={link.title}>
                              {property.displayValue}
                            </Link>
                          ) : (
                            property.displayValue
                          )}
                        </DescriptionListItem>
                      );
                    })}
                  </DescriptionList>
                </section>
              );
            })}
          </div>
        ),
      });
    }

    return result;
  }, [enhancedProperties, propertyLink, tComponents]);

  if (tabs.length === 0) return null;

  return (
    <Tabs
      id="vehicle-details-tabs"
      testId="vehicle-details-tabs"
      tabs={tabs}
      overflowPadding="var(--container-gutter)"
      overflowPaddingScope="lt-md"
    />
  );
};
